// src/components/profile/ChangePasswordForm.jsx
import React, { useState, useMemo } from "react";
import { changePassword } from "../../services/profile";
import { useToast } from "../ui/ToastContext";

/* Fuerza 0..4 */
function estimateStrength(pw) {
  let s = 0;
  if (!pw) return 0;
  if (pw.length >= 8) s++;
  if (pw.length >= 12) s++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) s++;
  if (/\d/.test(pw)) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  return Math.min(4, s);
}

function StrengthPills({ value }) {
  const total = 5;
  return (
    <div className="mt-2">
      <div
        className="flex gap-1.5"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={4}
        aria-valuenow={value}
      >
        {Array.from({ length: total }).map((_, i) => (
          <span
            key={i}
            className={`h-1.5 flex-1 rounded-full transition-all ${
              i < value ? "bg-emerald-600" : "bg-gray-200"
            }`}
          />
        ))}
      </div>
      <p className="mt-1 text-xs text-gray-600" aria-live="polite">
        {["Muy débil", "Débil", "Aceptable", "Buena", "Fuerte"][value] || "Muy débil"}
      </p>
    </div>
  );
}

function PolicyChecklist({ pw, cur }) {
  const rules = useMemo(
    () => [
      { ok: pw.length >= 8, label: "Mínimo 8 caracteres" },
      { ok: /[A-Z]/.test(pw) && /[a-z]/.test(pw), label: "Mayúsculas y minúsculas" },
      { ok: /\d/.test(pw), label: "Al menos un número" },
      { ok: /[^A-Za-z0-9]/.test(pw), label: "Al menos un símbolo" },
      { ok: pw && pw !== cur, label: "Distinta a la actual" },
    ],
    [pw, cur]
  );
  return (
    <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm" aria-live="polite">
      {rules.map((r, i) => (
        <li key={i} className="flex items-center gap-2">
          <span
            className={`inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[10px] ${
              r.ok ? "bg-emerald-600 text-white" : "bg-gray-200 text-gray-500"
            }`}
            aria-hidden="true"
          >
            {r.ok ? "✓" : "•"}
          </span>
          <span className={r.ok ? "text-gray-700" : "text-gray-500"}>{r.label}</span>
        </li>
      ))}
    </ul>
  );
}

export default function ChangePasswordForm() {
  const { show } = useToast(); // ← hook directo (provider presente)
  const [cur, setCur] = useState("");
  const [p1, setP1] = useState("");
  const [p2, setP2] = useState("");
  const [showCur, setShowCur] = useState(false);
  const [showP1, setShowP1] = useState(false);
  const [showP2, setShowP2] = useState(false);
  const [busy, setBusy] = useState(false);
  const [errMsg, setErrMsg] = useState("");

  const strength = estimateStrength(p1);
  const valid =
    cur.length > 0 && p1.length >= 8 && p1 === p2 && p1 !== cur && strength >= 2 && !busy;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!valid) return;
    try {
      setBusy(true);
      setErrMsg("");
      await changePassword(cur, p1);
      setCur(""); setP1(""); setP2("");
      show("Contraseña actualizada correctamente");
    } catch (err) {
      const m = err?.message || "No se pudo actualizar la contraseña";
      setErrMsg(m);
      show(m);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      aria-labelledby="cp-title"
      className="relative max-w-lg rounded-2xl border border-white/60 bg-white/90 backdrop-blur p-6 shadow-[0_10px_30px_rgba(0,0,0,0.08)]"
    >
      <h2 id="cp-title" className="text-2xl font-bold tracking-tight text-gray-900">Cambiar contraseña</h2>
      <p className="text-sm text-gray-600 mt-1">
        Recomendamos usar una frase larga y única. La nueva contraseña debe tener al menos 8 caracteres.
      </p>

      <form onSubmit={onSubmit} className="mt-5 space-y-5">
        {/* Actual */}
        <label className="block">
          <span className="text-sm text-gray-700">Contraseña actual</span>
          <div className="mt-1 relative">
            <input
              type={showCur ? "text" : "password"}
              value={cur}
              onChange={(e) => setCur(e.target.value)}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 pr-10 outline-none focus:ring-2 focus:ring-[#004B87]"
              autoComplete="current-password"
              required
            />
            <button
              type="button"
              onClick={() => setShowCur((v) => !v)}
              aria-pressed={showCur}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-sm text-gray-600"
              title={showCur ? "Ocultar" : "Mostrar"}
              aria-label={showCur ? "Ocultar contraseña actual" : "Mostrar contraseña actual"}
            >
              {showCur ? "🙈" : "👁️"}
            </button>
          </div>
        </label>

        {/* Nueva */}
        <label className="block">
          <span className="text-sm text-gray-700">Nueva contraseña</span>
          <div className="mt-1 relative">
            <input
              type={showP1 ? "text" : "password"}
              value={p1}
              onChange={(e) => setP1(e.target.value)}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 pr-10 outline-none focus:ring-2 focus:ring-[#004B87]"
              autoComplete="new-password"
              minLength={8}
              required
            />
            <button
              type="button"
              onClick={() => setShowP1((v) => !v)}
              aria-pressed={showP1}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-sm text-gray-600"
              title={showP1 ? "Ocultar" : "Mostrar"}
              aria-label={showP1 ? "Ocultar nueva contraseña" : "Mostrar nueva contraseña"}
            >
              {showP1 ? "🙈" : "👁️"}
            </button>
          </div>

          <StrengthPills value={strength} />
          <PolicyChecklist pw={p1} cur={cur} />
        </label>

        {/* Repetir */}
        <label className="block">
          <span className="text-sm text-gray-700">Repite la nueva contraseña</span>
          <div className="mt-1 relative">
            <input
              type={showP2 ? "text" : "password"}
              value={p2}
              onChange={(e) => setP2(e.target.value)}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 pr-10 outline-none focus:ring-2 focus:ring-[#004B87]"
              autoComplete="new-password"
              minLength={8}
              required
            />
            <button
              type="button"
              onClick={() => setShowP2((v) => !v)}
              aria-pressed={showP2}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-sm text-gray-600"
              title={showP2 ? "Ocultar" : "Mostrar"}
              aria-label={showP2 ? "Ocultar repetición de contraseña" : "Mostrar repetición de contraseña"}
            >
              {showP2 ? "🙈" : "👁️"}
            </button>
          </div>
          {p1 && p2 && p1 !== p2 && (
            <p className="text-sm text-red-600 mt-1" role="alert">Las contraseñas no coinciden.</p>
          )}
          {p1 && p1 === cur && (
            <p className="text-sm text-red-600 mt-1" role="alert">La nueva no puede ser igual a la actual.</p>
          )}
        </label>

        {errMsg && (
          <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-2">
            {errMsg}
          </div>
        )}

        <div className="pt-2 flex items-center gap-3">
          <button
            type="submit"
            disabled={!valid}
            className={`inline-flex items-center justify-center rounded-lg px-4 py-2 font-semibold text-white transition-all 
              ${valid ? "bg-[#004B87] hover:translate-y-[-1px] hover:shadow" : "bg-blue-300 cursor-not-allowed"}`}
          >
            {busy ? (
              <>
                <svg className="mr-2 h-4 w-4 animate-spin" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" opacity="0.25" />
                  <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="4" fill="none" />
                </svg>
                Guardando…
              </>
            ) : (
              "Actualizar contraseña"
            )}
          </button>
          <span className="text-xs text-gray-600 flex items-center gap-1">
            🛡️ Tu contraseña se transmite cifrada
          </span>
        </div>
      </form>
    </section>
  );
}